/**
 * experienceService -- persona tier + risk acknowledgements.
 *
 * Routes: GET/POST /v1/me/experience, POST/DELETE /v1/me/strategy-ack,
 * POST /v1/me/export-activity. All need Authorization: Bearer <jwt>.
 * Modal copy and versions come from the server (ackCopy); nothing is
 * duplicated on the client.
 */

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

export type ExperienceLevel = 'beginner' | 'intermediate' | 'expert';

export interface AckModalCopy {
  version: string;
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  checkboxLabel?: string;
}

export interface StoredAck {
  ackedAt?: string;
  modalVersion?: string;
}

export interface ExperienceState {
  experienceLevel: ExperienceLevel;
  strategyAcks: Record<string, StoredAck>;
  brokerTiers: Record<string, unknown>;
  capitalMinimumAcked: boolean;
  ackCopy: {
    tiers: Partial<Record<ExperienceLevel, AckModalCopy>>;
    strategies: Record<string, AckModalCopy>;
  };
}

export interface ExportResult {
  url: string;
  expiresAt: string;
  sha256: string;
  rowCount: number;
}

export interface ServiceError extends Error {
  status?: number;
  code?: string;
}

function headers(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

async function handle<T>(res: Response): Promise<T> {
  const body: any = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: ServiceError = new Error(
      (typeof body?.error === 'string' && body.error) || `HTTP ${res.status}`
    );
    err.status = res.status;
    err.code = body?.code;
    throw err;
  }
  return body as T;
}

export async function getExperience(token: string): Promise<ExperienceState> {
  const res = await fetch(`${API_BASE}/v1/me/experience`, { headers: headers(token) });
  return handle<ExperienceState>(res);
}

export async function setLevel(
  token: string,
  newLevel: ExperienceLevel,
  opts: { ackModalVersion?: string; capitalMinimumAcked?: boolean } = {}
): Promise<{ experienceLevel: ExperienceLevel }> {
  const res = await fetch(`${API_BASE}/v1/me/experience`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ newLevel, ...opts }),
  });
  return handle(res);
}

export async function recordAck(
  token: string,
  strategy: string,
  modalVersion: string
): Promise<{ acked: boolean; strategyAcks?: Record<string, StoredAck>; auditGap?: boolean }> {
  const res = await fetch(`${API_BASE}/v1/me/strategy-ack`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ strategy, modalVersion }),
  });
  return handle(res);
}

export async function removeAck(token: string, strategy: string): Promise<{ removed: boolean }> {
  const res = await fetch(`${API_BASE}/v1/me/strategy-ack`, {
    method: 'DELETE',
    headers: headers(token),
    body: JSON.stringify({ strategy }),
  });
  return handle(res);
}

export async function exportActivity(token: string): Promise<ExportResult> {
  const res = await fetch(`${API_BASE}/v1/me/export-activity`, {
    method: 'POST',
    headers: headers(token),
    body: '{}',
  });
  return handle<ExportResult>(res);
}
